import clsx from 'clsx';
import { useTranslation } from 'next-i18next/pages';
import React, { Key, ReactNode } from 'react';

import { CalculationType } from '@pflegia/types/api/job-position/calculation-type.enum';
import { SalaryElement } from '@pflegia/types/api/job-position/salary-element';
import { EducationPayment } from '@pflegia/types/api/payment/education-payment';
import { ExtraPayment } from '@pflegia/types/api/payment/extra-payment';
import { monthlyHours } from '@pflegia/util/functions/salary';

import ReadMore from '../job-position/ReadMore';
import { SalaryDetailItem } from '../salary-detail-item/salary-detail-item.component';
import Section, { SectionTitle } from '../section/Section';
import { BasicSalaryDetails } from './BasicSalaryDetails.component';
import { SalaryTabs } from './SalaryTabs.component';
import { CustomSurgeIncomeElement, SurgeIncome } from './SurgeIncome.component';
import getSurgeIncomeWithCalculatedValues from './getSurgeIncomeWithCalculatedValues';

export const NUM_OF_MONTHS_IN_YEAR = 12;

interface SalaryDetailsProps {
  salaryHigh: number;
  salaryLow: number;
  showApproximationForSingleValue?: boolean;
  formalLanguage?: boolean;
  educationBonuses: EducationPayment[];
  specialBonuses: ExtraPayment[];
  surgeIncomes: SalaryElement[];
  customSurgeIncomes?: CustomSurgeIncomeElement[];
  oneYearExperience: number;
  threeYearExperience: number;
  eightYearExperience: number;
  fullTimeHours: number;
  showSalaryDetails: boolean;
  shiftDurationHours?: number;
  customBrandingStyle?: {
    mainColor?: string;
    secondaryColor?: string;
    lightColor?: string;
  };
  additionalSalaryInformation?: string;
}

export const SalaryDetails: React.FC<SalaryDetailsProps> = ({
  salaryHigh,
  salaryLow,
  showApproximationForSingleValue,
  formalLanguage,
  educationBonuses,
  specialBonuses,
  surgeIncomes,
  customSurgeIncomes,
  oneYearExperience,
  threeYearExperience,
  eightYearExperience,
  fullTimeHours,
  showSalaryDetails,
  shiftDurationHours,
  customBrandingStyle,
  additionalSalaryInformation,
}) => {
  const { t } = useTranslation('jobDetails', { keyPrefix: 'salaryDetails' });
  const [calculationType, setCalculationType] = React.useState<CalculationType>(CalculationType.Monthly);

  let calculatedSalaryLow;
  let calculatedSalaryHigh;

  const hours = monthlyHours(fullTimeHours);

  switch (calculationType) {
    case CalculationType.Hourly:
      calculatedSalaryLow = (salaryLow / hours).toFixed(2);
      calculatedSalaryHigh = salaryHigh ? (salaryHigh / hours).toFixed(2) : 0;
      break;
    case CalculationType.Monthly:
      calculatedSalaryLow = salaryLow;
      calculatedSalaryHigh = salaryHigh;
      break;
    case CalculationType.Yearly:
      calculatedSalaryHigh = salaryHigh ? salaryHigh * NUM_OF_MONTHS_IN_YEAR : 0;
      calculatedSalaryLow = salaryLow * NUM_OF_MONTHS_IN_YEAR;
      break;
    default:
      calculatedSalaryLow = salaryLow;
      calculatedSalaryHigh = salaryHigh;
      break;
  }
  const hasBasicSalaryDetails = oneYearExperience || threeYearExperience || eightYearExperience;
  const hasSalaryLow = Number(calculatedSalaryLow) > 0;
  const hasSalaryHigh = Number(calculatedSalaryHigh) > 0;
  const hasSalaryRange = hasSalaryLow && hasSalaryHigh;
  const singleSalaryValue = hasSalaryLow ? calculatedSalaryLow : calculatedSalaryHigh;
  const showSingleSalaryValue = !hasSalaryRange && Number(singleSalaryValue) > 0;

  const salaryDetailItemComponents: { component: ReactNode; key: Key }[] = [];

  if (hasBasicSalaryDetails) {
    salaryDetailItemComponents.push({
      component: (
        <BasicSalaryDetails
          oneYearExperience={oneYearExperience}
          threeYearExperience={threeYearExperience}
          eightYearExperience={eightYearExperience}
        />
      ),
      key: 'basic',
    });
  }

  const surgeIncomeElements: (SalaryElement | CustomSurgeIncomeElement)[] =
    (shiftDurationHours || shiftDurationHours === 0) && fullTimeHours
      ? getSurgeIncomeWithCalculatedValues({
          surgeIncomeElements: surgeIncomes,
          fullTimeHours,
          threeYearExperience,
          shiftDurationHours,
        })
      : [];

  if (customSurgeIncomes?.length) surgeIncomeElements.push(...customSurgeIncomes);

  if (surgeIncomeElements.length) {
    salaryDetailItemComponents.push({
      component: <SurgeIncome surgeIncomeElements={surgeIncomeElements} />,
      key: 'surge',
    });
  }

  if (educationBonuses?.length && educationBonuses[0]?.value > 0) {
    salaryDetailItemComponents.push({
      component: <SalaryDetailItem title={`🎓 ${t('educationBonuses')}`} bonusElements={educationBonuses} />,
      key: 'education',
    });
  }

  if (specialBonuses?.length && specialBonuses[0]?.value > 0) {
    salaryDetailItemComponents.push({
      component: <SalaryDetailItem title={`😀 ${t('specialBonuses')}`} bonusElements={specialBonuses} />,
      key: 'bonuses',
    });
  }

  const salaryExpectationTitle = hasSalaryRange ? 'salaryExpectationTitle' : 'salaryExpectationTitleSingleValue';
  const salaryRangeAvailable = hasSalaryRange || showSingleSalaryValue;

  return (
    <Section className='px-4 pt-4 pb-6 md:px-12 md:pb-12'>
      <SectionTitle noMargin t={t} titleKey='title' />
      <div className='ml-2 flex flex-row py-2 md:ml-0'>
        <SalaryTabs setCalculationType={setCalculationType} fullTimeHours={fullTimeHours} />
      </div>
      <div className='border-t'>
        {salaryRangeAvailable && (
          <p className='text-normal text-main-blue mt-4 font-medium'>
            {t(`${salaryExpectationTitle}.${formalLanguage ? 'formal' : 'informal'}`)}
          </p>
        )}
      </div>
      {salaryRangeAvailable && (
        <>
          <div className='mt-6 text-center md:mt-3'>
            <p className='text-normal text-main-blue mb-4 text-3xl font-bold md:mb-3 md:text-5xl'>
              {hasSalaryRange ? (
                <>
                  <span>{calculatedSalaryLow.toLocaleString('de')}€</span>
                  {' - '}
                  <span>{calculatedSalaryHigh.toLocaleString('de')}€</span>
                </>
              ) : (
                <span>
                  {showApproximationForSingleValue ? '≈ ' : ''}
                  {singleSalaryValue.toLocaleString('de')}€
                </span>
              )}
            </p>
          </div>

          <div className='relative mb-8 flex flex-col items-center'>
            <div
              className={clsx(
                'bg-primary-badge absolute right-0 left-0 mx-auto h-3 w-full rounded-xl lg:w-4/5',
                customBrandingStyle?.lightColor,
              )}
            />
            <div className={clsx('bg-main-blue absolute mx-auto my-0 h-3 w-[57%] rounded-xl md:w-[46%]', customBrandingStyle?.mainColor)} />
          </div>
        </>
      )}

      {additionalSalaryInformation && (
        <div
          className={clsx('mt-10 rounded-lg bg-blue-50 p-6', showSalaryDetails && Boolean(salaryDetailItemComponents) ? 'mb-7' : 'mb-2')}>
          <ReadMore renderHtml text={additionalSalaryInformation} className='leading-7 text-blue-900/90!' />
        </div>
      )}

      {/* for layouts other than mobile */}
      {showSalaryDetails && Boolean(salaryDetailItemComponents) && (
        <>
          <div className='flex flex-col md:hidden'>
            {salaryDetailItemComponents.map(({ component, key }) => (
              <div className='border-t' key={key}>
                {component}
              </div>
            ))}
          </div>
          <div className='hidden md:flex md:flex-col'>
            {salaryDetailItemComponents.map((_, index) => (
              <React.Fragment key={salaryDetailItemComponents[index].key}>
                {index % 2 === 0 && (
                  <>
                    <div className='border-t' />
                    <div className='md:flex md:flex-row'>
                      <div className='w-1/2'>{salaryDetailItemComponents[index].component}</div>
                      {salaryDetailItemComponents[index + 1] && (
                        <>
                          <div className='mx-8 my-6 border-r' />
                          <div className='w-1/2'>{salaryDetailItemComponents[index + 1].component}</div>
                        </>
                      )}
                    </div>
                  </>
                )}
              </React.Fragment>
            ))}
          </div>
        </>
      )}
    </Section>
  );
};

export default SalaryDetails;
